import React, { useState, useEffect, useCallback } from 'react';
import { placementsAPI } from '../services/api';
import PlacementCard from '../components/PlacementCard';
import FilterPanel from '../components/FilterPanel';
import '../styles/PlacementsPage.css';

const PlacementsPage = () => {
  const [placements, setPlacements] = useState([]);
  const [filteredPlacements, setFilteredPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    company: '',
    role: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    companies: [],
    roles: []
  });

  const applyFilters = useCallback(() => {
    // Ensure placements is an array before filtering
    if (!Array.isArray(placements)) {
      setFilteredPlacements([]);
      return;
    }

    let filtered = placements;

    if (filters.company) {
      filtered = filtered.filter(p =>
        p.companyName && p.companyName.toLowerCase().includes(filters.company.toLowerCase())
      );
    }

    if (filters.role) {
      filtered = filtered.filter(p =>
        p.role && p.role.toLowerCase().includes(filters.role.toLowerCase())
      );
    }

    setFilteredPlacements(filtered);
  }, [placements, filters]);

  useEffect(() => {
    fetchPlacements();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const fetchPlacements = async () => {
    try {
      setLoading(true);
      const response = await placementsAPI.getAll();
      // The new Firebase API returns data directly as an array
      setPlacements(response.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch placements');
      console.error('Error fetching placements:', err);
      setPlacements([]); // Set empty array on error to prevent crashes
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await placementsAPI.getFilterOptions();
      setFilterOptions(response.data);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({
      company: '',
      role: ''
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading placements...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button onClick={fetchPlacements}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="placements-page">
      <div className="page-header">
        <h1>Placement Experiences</h1>
        <p>Discover real placement experiences from students across various companies</p>
      </div>

      <FilterPanel
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={clearFilters}
        type="placements"
      />

      <div className="results-info">
        <p>Showing {filteredPlacements?.length || 0} of {placements?.length || 0} experiences</p>
      </div>

      <div className="placements-grid">
        {filteredPlacements && filteredPlacements.length > 0 ? (
          filteredPlacements.map(placement => (
            <PlacementCard key={placement.id} placement={placement} />
          ))
        ) : (
          <div className="no-results">
            <p>No placements found matching your criteria.</p>
            <button onClick={clearFilters}>Clear Filters</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlacementsPage;
